import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  ShieldCheck, PenLine, Eye, BadgeCheck, Users, Save, Mail, IdCard, Briefcase, Cake,
} from "lucide-react";
import { toast } from "sonner";

const ROLE_META = {
  ADMIN: { label: "Admin", icon: ShieldCheck, desc: "Full control over every module, users and approvals." },
  CREATOR: { label: "Creator", icon: PenLine, desc: "Create work items and move them To Do → Prepare → Create → Review." },
  REVIEWER: { label: "Reviewer", icon: Eye, desc: "Review submitted work and send it back to Create or on to Approval." },
  FINAL_APPROVER: { label: "Final Approver", icon: BadgeCheck, desc: "Give the final approval that completes a work item." },
};

const initialsOf = (name) => (name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

export default function Profile() {
  const { user, setUser } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [designation, setDesignation] = useState(user?.designation || "");
  const [saving, setSaving] = useState(false);
  const [team, setTeam] = useState([]);

  useEffect(() => {
    setName(user?.name || "");
    setDesignation(user?.designation || "");
  }, [user]);

  useEffect(() => {
    api.get("/team").then((r) => setTeam(r.data)).catch(() => {});
  }, []);

  const myRoles = user?.roles || [];
  const dirty = name.trim() !== (user?.name || "") || (designation.trim() || "") !== (user?.designation || "");

  const save = async () => {
    if (!name.trim()) return toast.error("Name cannot be empty");
    setSaving(true);
    try {
      const { data } = await api.patch("/profile", { name: name.trim(), designation: designation.trim() });
      setUser(data);
      toast.success("Profile updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update profile");
    } finally {
      setSaving(false);
    }
  };

  const byRole = (role) => team.filter((m) => (m.roles || []).includes(role));
  const RoleGroup = ({ role }) => {
    const meta = ROLE_META[role];
    const members = byRole(role);
    return (
      <div data-testid={`team-group-${role}`} className="rounded-xl border border-border bg-card p-4">
        <div className="flex items-center gap-2 mb-3">
          <meta.icon className="h-4 w-4 text-primary" />
          <p className="font-semibold text-sm">{meta.label}</p>
          <span className="ml-auto text-xs text-muted-foreground">{members.length}</span>
        </div>
        {members.length === 0 ? (
          <p className="text-xs text-muted-foreground">No one assigned.</p>
        ) : (
          <ul className="space-y-2">
            {members.map((m) => (
              <li key={m.user_id} className="flex items-center gap-2.5">
                <Avatar className="h-7 w-7 border border-border">
                  <AvatarImage src={m.picture} alt={m.name} />
                  <AvatarFallback className="bg-primary/10 text-primary text-[10px] font-semibold">{initialsOf(m.name)}</AvatarFallback>
                </Avatar>
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{m.name}{m.user_id === user?.user_id && <span className="text-muted-foreground font-normal"> · you</span>}</p>
                  <p className="text-[11px] text-muted-foreground truncate">{m.designation || "—"}</p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  };

  return (
    <div className="fade-up space-y-6 max-w-4xl" data-testid="profile-page">
      <div>
        <h2 className="font-display text-2xl font-bold tracking-tight">My Profile</h2>
        <p className="text-muted-foreground text-sm mt-1">Update your details, review what you can do, and see who reviews and approves work.</p>
      </div>

      {/* Identity + edit */}
      <div className="rounded-xl border border-border bg-card p-5 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <Avatar className="h-16 w-16 border border-border">
            <AvatarImage src={user?.picture} alt={user?.name} />
            <AvatarFallback className="bg-primary/10 text-primary font-bold text-lg">{initialsOf(user?.name)}</AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <p className="font-display text-lg font-bold truncate">{user?.name}</p>
            <p className="text-sm text-muted-foreground truncate">{user?.designation || "No designation set"}</p>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4 mt-6">
          <div>
            <Label className="flex items-center gap-1.5 text-xs"><IdCard className="h-3.5 w-3.5" /> Display Name</Label>
            <Input data-testid="profile-name-input" value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5" placeholder="Your name" />
          </div>
          <div>
            <Label className="flex items-center gap-1.5 text-xs"><Briefcase className="h-3.5 w-3.5" /> Designation</Label>
            <Input data-testid="profile-designation-input" value={designation} onChange={(e) => setDesignation(e.target.value)} className="mt-1.5" placeholder="e.g. Video Editor" />
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4 mt-4">
          <div>
            <Label className="flex items-center gap-1.5 text-xs text-muted-foreground"><Mail className="h-3.5 w-3.5" /> Email</Label>
            <p className="mt-1.5 text-sm rounded-lg border border-border bg-muted/40 px-3 py-2 truncate" data-testid="profile-email">{user?.email}</p>
          </div>
          <div>
            <Label className="flex items-center gap-1.5 text-xs text-muted-foreground"><IdCard className="h-3.5 w-3.5" /> User ID</Label>
            <p className="mt-1.5 text-sm font-mono text-primary rounded-lg border border-border bg-muted/40 px-3 py-2" data-testid="profile-user-code">{user?.user_code || "—"}</p>
          </div>
        </div>

        <div className="grid sm:grid-cols-3 gap-4 mt-4">
          <div>
            <Label className="flex items-center gap-1.5 text-xs text-muted-foreground"><BadgeCheck className="h-3.5 w-3.5" /> Employee ID</Label>
            <p className="mt-1.5 text-sm font-mono rounded-lg border border-border bg-muted/40 px-3 py-2" data-testid="profile-employee-id">{user?.employee_id || "—"}</p>
          </div>
          <div>
            <Label className="flex items-center gap-1.5 text-xs text-muted-foreground"><Briefcase className="h-3.5 w-3.5" /> Department</Label>
            <p className="mt-1.5 text-sm rounded-lg border border-border bg-muted/40 px-3 py-2 truncate" data-testid="profile-department">{user?.department || "—"}</p>
          </div>
          <div>
            <Label className="flex items-center gap-1.5 text-xs text-muted-foreground"><Cake className="h-3.5 w-3.5" /> Birthday</Label>
            <p className="mt-1.5 text-sm rounded-lg border border-border bg-muted/40 px-3 py-2" data-testid="profile-birthday">{user?.birthday ? new Date(2000, parseInt(user.birthday.slice(0, 2), 10) - 1, parseInt(user.birthday.slice(3, 5), 10)).toLocaleDateString("en-IN", { day: "numeric", month: "long" }) : "—"}</p>
          </div>
          <div>
            <Label className="flex items-center gap-1.5 text-xs text-muted-foreground"><Briefcase className="h-3.5 w-3.5" /> Sub-Department</Label>
            <p className="mt-1.5 text-sm rounded-lg border border-border bg-muted/40 px-3 py-2 truncate" data-testid="profile-sub-department">{user?.sub_department || "—"}</p>
          </div>
        </div>

        <div className="flex justify-end mt-5">
          <Button data-testid="profile-save-button" onClick={save} disabled={!dirty || saving} className="gap-1.5">
            <Save className="h-4 w-4" /> {saving ? "Saving…" : "Save changes"}
          </Button>
        </div>
        <p className="text-[11px] text-muted-foreground mt-2">Email and roles are managed by your administrator in Access Control.</p>
      </div>

      {/* My permissions */}
      <div className="rounded-xl border border-border bg-card p-5 sm:p-6" data-testid="profile-permissions">
        <div className="flex items-center gap-2 mb-4">
          <ShieldCheck className="h-4.5 w-4.5 text-primary" />
          <h3 className="font-display font-bold">My permissions</h3>
        </div>
        {myRoles.length === 0 ? (
          <p className="text-sm text-muted-foreground">No roles assigned yet.</p>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3">
            {myRoles.map((r) => {
              const meta = ROLE_META[r] || { label: r, icon: BadgeCheck, desc: "" };
              return (
                <div key={r} data-testid={`my-role-${r}`} className="flex items-start gap-3 rounded-lg border border-border p-3">
                  <div className="h-8 w-8 shrink-0 rounded-lg bg-primary/10 flex items-center justify-center">
                    <meta.icon className="h-4 w-4 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold">{meta.label}</p>
                    <p className="text-xs text-muted-foreground leading-snug">{meta.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Team roles */}
      <div className="rounded-xl border border-border bg-card p-5 sm:p-6" data-testid="profile-team">
        <div className="flex items-center gap-2 mb-4">
          <Users className="h-4.5 w-4.5 text-primary" />
          <h3 className="font-display font-bold">Who reviews & approves</h3>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <RoleGroup role="REVIEWER" />
          <RoleGroup role="FINAL_APPROVER" />
          <RoleGroup role="ADMIN" />
          <RoleGroup role="CREATOR" />
        </div>
      </div>
    </div>
  );
}
